import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import Masonry from 'react-masonry-css';
import SEO from '../components/SEO';
import ProfileCard from '../components/ProfileCard';
import { useInfiniteProfilesList } from '../api/profiles';
import { useCategoriesTree } from '../api/categories';
import CategoryTreeNav from '../components/CategoryTreeNav';
import { BRAND } from '../config/brand';

function buildCategoryIndex(tree) {
  const bySlug = new Map();
  const byId = new Map();
  const walk = (n, parent = null) => {
    if (!n) return;
    const node = {
      ...n,
      id: String(n.id),
      slug: String(n.slug || ''),
      level: Number(n.level || 0),
      parent
    };
    byId.set(node.id, node);
    if (node.slug) bySlug.set(node.slug, node);
    const kids = Array.isArray(n.children) ? n.children : [];
    kids.forEach((c) => walk(c, node));
  };
  (Array.isArray(tree) ? tree : []).forEach((r) => walk(r, null));
  return { bySlug, byId };
}

function selectedIdsFromNode(node) {
  if (!node) return null;
  const level = Number(node.level);
  if (level === 1) return { main: node.id };
  if (level === 2) return { sub: node.id };
  if (level === 3) return { subSub: node.id };
  return null;
}

const Feed = () => {
  const [params, setParams] = useSearchParams();
  const q = (params.get('q') || '').trim();
  const categorySlug = (params.get('category') || '').trim();

  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);

  // Simple swipe gesture state (used for bottom-sheet open/close)
  const touchStartYRef = React.useRef(null);
  const touchActiveRef = React.useRef(false);
  const SWIPE_OPEN_PX = 28;
  const SWIPE_CLOSE_PX = 28;

  const { data: categoriesTreeData } = useCategoriesTree();
  const categoriesTree = useMemo(() => {
    const t = categoriesTreeData?.tree;
    return Array.isArray(t) ? t : [];
  }, [categoriesTreeData?.tree]);
  const categoryIndex = useMemo(() => buildCategoryIndex(categoriesTree), [categoriesTree]);
  const selectedNode = useMemo(() => {
    if (!categorySlug) return null;
    return categoryIndex.bySlug.get(categorySlug) || null;
  }, [categorySlug, categoryIndex]);

  // For mobile quick chips we want the top-most parent (root) selected.
  const selectedRootNode = useMemo(() => {
    let n = selectedNode;
    while (n?.parent) n = n.parent;
    return n || null;
  }, [selectedNode]);
  const selectedIds = useMemo(() => selectedIdsFromNode(selectedNode), [selectedNode]);

  // If URL has unknown slug (e.g. outdated), remove it once tree is loaded.
  React.useEffect(() => {
    if (!categorySlug) return;
    if (!categoriesTreeData) return; // not loaded yet
    if (selectedNode) return;
    const next = new URLSearchParams(params);
    next.delete('category');
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorySlug, categoriesTreeData, selectedNode]);

  // Mobile drawer: prevent background scroll.
  React.useEffect(() => {
    if (!mobileCatsOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileCatsOpen]);

  const onSheetHandleTouchStart = (e) => {
    const y = e.touches?.[0]?.clientY;
    if (typeof y !== 'number') return;
    touchStartYRef.current = y;
    touchActiveRef.current = true;
  };

  const onSheetHandleTouchMove = (e) => {
    if (!touchActiveRef.current) return;
    const y = e.touches?.[0]?.clientY;
    const startY = touchStartYRef.current;
    if (typeof y !== 'number' || typeof startY !== 'number') return;
    const dy = y - startY;
    if (dy > SWIPE_CLOSE_PX) {
      touchActiveRef.current = false;
      setMobileCatsOpen(false);
    }
  };

  const onSheetHandleTouchEnd = () => {
    touchActiveRef.current = false;
    touchStartYRef.current = null;
  };

  const onPeekTouchStart = (e) => {
    const y = e.touches?.[0]?.clientY;
    if (typeof y !== 'number') return;
    touchStartYRef.current = y;
    touchActiveRef.current = true;
  };

  const onPeekTouchMove = (e) => {
    if (!touchActiveRef.current) return;
    const y = e.touches?.[0]?.clientY;
    const startY = touchStartYRef.current;
    if (typeof y !== 'number' || typeof startY !== 'number') return;
    const dy = y - startY;
    // Swipe up => negative delta
    if (dy < -SWIPE_OPEN_PX) {
      touchActiveRef.current = false;
      setMobileCatsOpen(true);
    }
  };

  const onPeekTouchEnd = () => {
    touchActiveRef.current = false;
    touchStartYRef.current = null;
  };

  const { ref: sentinelRef, inView } = useInView({ rootMargin: '0px 0px 400px 0px' });

  const queryParams = useMemo(() => {
    const out = { limit: 24 };
    // Prefer server-side sort so infinite-scroll does not reshuffle between pages.
    out.sort = 'cover';
    // Requirement: promoted profiles should always be on top in feed
    out.promoted_first = 1;
    if (q) out.q = q;
    if (selectedNode) {
      const level = Number(selectedNode.level);
      // Backend semantics:
      // - main filter also includes profiles assigned to sub/subSub (because chain includes main)
      // - sub filter includes subSub (because chain includes sub)
      if (level === 1) out.main = selectedNode.id;
      if (level === 2) out.sub = selectedNode.id;
      if (level === 3) out.subSub = selectedNode.id;
    }
    return out;
  }, [q, selectedNode]);

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteProfilesList(queryParams);

  const pages = Array.isArray(data?.pages) ? data.pages : [];
  const profiles = pages.flatMap((p) => (Array.isArray(p?.profiles) ? p.profiles : []));

  // Server sorts by cover when sort=cover, so keep order as-is.
  const orderedProfiles = profiles;

  const breakpointColumns = {
    default: 3,
    1400: 3,
    1100: 3,
    700: 2,
    500: 1
  };

  // Infinite scroll trigger
  React.useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Keep URL clean: we no longer use page= in infinite mode.
  React.useEffect(() => {
    if (params.get('page')) {
      const next = new URLSearchParams(params);
      next.delete('page');
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setCategorySlug = (slugOrNull) => {
    const next = new URLSearchParams(params);
    next.delete('page');
    if (slugOrNull) next.set('category', String(slugOrNull));
    else next.delete('category');
    setParams(next);
  };

  const onSelectCategory = (node) => {
    if (!node?.slug) return;
    setCategorySlug(node.slug);
    setMobileCatsOpen(false);
  };

  const onSelectCategoryKeepOpen = (node) => {
    if (!node?.slug) return;
    setCategorySlug(node.slug);
    // keep mobile sheet open (for browsing subcategories)
  };

  const onClearCategory = () => {
    setCategorySlug(null);
    setMobileCatsOpen(false);
  };

  const onClearCategoryKeepOpen = () => {
    setCategorySlug(null);
    // keep mobile sheet open
  };

  const topCategories = useMemo(() => {
    // Root categories only (level 1). Use them as quick-access chips on mobile.
    return (Array.isArray(categoriesTree) ? categoriesTree : []).filter((n) => n?.slug);
  }, [categoriesTree]);

  const mobileTree = useMemo(() => {
    // UX: When a root category is selected, show only its subtree.
    if (!selectedRootNode?.id) return categoriesTree;
    const root = topCategories.find((c) => String(c.id) === String(selectedRootNode.id));
    return root ? [root] : categoriesTree;
  }, [categoriesTree, topCategories, selectedRootNode]);

  if (isLoading) {
    return (
      <>
        <SEO
          title="Лента — арт-профили"
          description={`Лента арт-профилей на ${BRAND.name}.`}
          url="/feed"
          type="website"
          canonical={`${BRAND.baseUrl}/feed`}
          noindex={true}
        />
        <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
            <p className="text-secondary-600">Загрузка...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SEO
          title="Лента — профили"
          description={`Лента арт-профилей на ${BRAND.name}.`}
          url="/feed"
          type="website"
          canonical={`${BRAND.baseUrl}/feed`}
          noindex={true}
        />
        <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">😔</div>
            <h2 className="text-2xl font-semibold text-secondary-900 mb-2">
              Что-то пошло не так
            </h2>
            <p className="text-secondary-600 mb-4">
              Не удалось загрузить арт-профили. Попробуйте обновить страницу.
            </p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Лента — профили"
        description={`Лента арт-профилей на ${BRAND.name}.`}
        url="/feed"
        type="website"
        canonical={`${BRAND.baseUrl}/feed`}
        noindex={true}
      />
      <div className="max-w-7xl mx-auto bg-secondary-50">
        <div className="px-4 py-8">
          <div className="flex items-start gap-6">
            {/* Desktop: left categories tree */}
            <div className="hidden lg:block w-80 shrink-0">
              <div className="sticky top-20">
                <CategoryTreeNav
                  tree={categoriesTree}
                  selected={selectedIds}
                  onSelect={onSelectCategory}
                  onClear={onClearCategory}
                  maxHeight="calc(100vh - 6.5rem)"
                />
              </div>
            </div>

            {/* Main feed */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-4 gap-3">
                <div className="min-w-0">
                  <h1 className="text-2xl font-semibold text-secondary-900">Лента арт-профилей</h1>
                  {/* Desktop/Tablet: show category + reset inline */}
                  {selectedNode?.name && (
                    <div className="mt-1 text-sm text-secondary-600 items-center gap-2 flex-wrap hidden sm:flex">
                      <span className="truncate">Категория: <span className="text-secondary-900 font-medium">{selectedNode.name}</span></span>
                      <button
                        type="button"
                        onClick={onClearCategory}
                        className="text-xs px-2 py-1 rounded-full border border-secondary-200 bg-white hover:bg-secondary-50"
                      >
                        Сбросить
                      </button>
                    </div>
                  )}
                </div>

                {/* Mobile: categories control bar */}
                <div className="lg:hidden flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setMobileCatsOpen(true)}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-secondary-200 bg-white hover:bg-secondary-50 text-sm text-secondary-800 max-w-[70vw]"
                    aria-label="Открыть категории"
                  >
                    <span className="truncate">
                      {selectedNode?.name ? selectedNode.name : 'Категории'}
                    </span>
                    <span className="text-secondary-500">▾</span>
                  </button>

                  {selectedNode?.name && (
                    <button
                      type="button"
                      onClick={onClearCategory}
                      className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl border border-secondary-200 bg-white hover:bg-secondary-50 text-secondary-700"
                      aria-label="Сбросить категорию"
                      title="Сбросить"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {profiles.length > 0 ? (
                <Masonry
                  breakpointCols={breakpointColumns}
                  className="masonry-grid"
                  columnClassName="masonry-grid_column"
                >
                  {orderedProfiles.map((p) => (
                    <div key={p.id} className="mb-6">
                      <ProfileCard profile={p} />
                    </div>
                  ))}
                </Masonry>
              ) : (
                <div className="text-center py-16">
                  <div className="text-6xl mb-4">👤</div>
                  <h3 className="text-xl font-semibold text-secondary-900 mb-2">Пока нет профилей</h3>
                  <p className="text-secondary-600 mb-4">Проверьте фильтры или попробуйте позже.</p>
                </div>
              )}

              {/* Infinite Scroll Trigger */}
              {hasNextPage && (
                <div ref={sentinelRef} className="flex items-center justify-center py-8">
                  {isFetchingNextPage ? (
                    <div className="text-secondary-600">Загрузка еще...</div>
                  ) : (
                    <div className="text-secondary-500">Прокрутите для загрузки еще</div>
                  )}
                </div>
              )}

              {!hasNextPage && profiles.length > 0 && (
                <div className="text-center py-8">
                  <p className="text-secondary-500">Конец ленты! 🎉</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile bottom-sheet: categories */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden transition ${mobileCatsOpen ? 'pointer-events-auto' : 'pointer-events-none'
          }`}
        aria-hidden={!mobileCatsOpen}
      >
        {/* Backdrop */}
        <button
          type="button"
          aria-label="Закрыть"
          className={`absolute inset-0 bg-black/30 transition-opacity duration-200 ${mobileCatsOpen ? 'opacity-100' : 'opacity-0'
            }`}
          onClick={() => setMobileCatsOpen(false)}
          tabIndex={mobileCatsOpen ? 0 : -1}
        />

        {/* Sheet */}
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Категории"
          className={`absolute inset-x-0 bottom-0 bg-secondary-50 border-t border-secondary-200 rounded-t-2xl shadow-2xl max-h-[85vh] h-full overflow-hidden transition-transform duration-300 ease-out ${mobileCatsOpen ? 'translate-y-0' : 'translate-y-full'
            }`}
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
        >
          {/* Handle */}
          <div
            className="pt-2 pb-1 flex items-center justify-center"
            onTouchStart={onSheetHandleTouchStart}
            onTouchMove={onSheetHandleTouchMove}
            onTouchEnd={onSheetHandleTouchEnd}
            onTouchCancel={onSheetHandleTouchEnd}
          >
            <div className="h-1.5 w-12 rounded-full bg-secondary-300" />
          </div>

          {/* Header */}
          <div className="px-4 pb-3 border-b border-secondary-100">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-secondary-900">Категории</div>
                {selectedNode?.name ? (
                  <div className="mt-0.5 text-xs text-secondary-600 truncate">
                    Выбрано: <span className="text-secondary-900 font-medium">{selectedNode.name}</span>
                  </div>
                ) : (
                  <div className="mt-0.5 text-xs text-secondary-500">Выберите категорию для фильтра</div>
                )}
              </div>
              <button
                type="button"
                className="text-sm px-3 py-2 rounded-lg border border-secondary-200 bg-white hover:bg-secondary-50"
                onClick={() => setMobileCatsOpen(false)}
              >
                Готово
              </button>
            </div>

            {/* Quick chips */}
            <div className="mt-3 -mx-4 px-4 overflow-x-auto scrollbar-hide">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClearCategoryKeepOpen}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-sm border transition-colors ${selectedNode ? 'bg-white border-secondary-200 text-secondary-800' : 'bg-primary-50 border-primary-200 text-primary-800'
                    }`}
                >
                  Все
                </button>
                {topCategories.slice(0, 12).map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => onSelectCategoryKeepOpen(c)}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-sm border transition-colors ${selectedRootNode?.id === String(c.id)
                      ? 'bg-primary-50 border-primary-200 text-primary-800'
                      : 'bg-white border-secondary-200 text-secondary-800'
                      }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 overflow-hidden">
            <CategoryTreeNav
              tree={mobileTree}
              selected={selectedIds}
              onSelect={onSelectCategory}
              onClear={onClearCategory}
              showSearch={false}
              showHeader={false}
              variant="mobile"
              className="border-0 shadow-none"
              maxHeight="calc(85vh - 11.5rem)"
            />
          </div>
        </div>
      </div>

      {/* Mobile peek area when closed: swipe up to open */}
      {!mobileCatsOpen && (
        <div className="fixed inset-x-0 bottom-0 z-[55] lg:hidden pointer-events-auto">
          <div
            className="mx-auto w-full max-w-7xl px-4 pb-2"
            onTouchStart={onPeekTouchStart}
            onTouchMove={onPeekTouchMove}
            onTouchEnd={onPeekTouchEnd}
            onTouchCancel={onPeekTouchEnd}
          >
            <button
              type="button"
              onClick={() => setMobileCatsOpen(true)}
              className="w-full bg-white/90 backdrop-blur border border-secondary-200 rounded-2xl shadow-soft px-4 py-3 flex items-center justify-between"
              aria-label="Открыть категории"
            >
              <span className="text-sm font-medium text-secondary-900">Категории</span>
              <span className="text-secondary-500">↑</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Feed;
